import { useEffect } from 'react'
import { X, Github, ExternalLink, ChevronRight } from 'lucide-react'

export default function ProjectModal({ project, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onClose])

  if (!project) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${project.title} details`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 modal-overlay" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-[#1e293b] bg-[#0d0d0d] shadow-2xl shadow-black/70">

        {/* Sticky header */}
        <div className="sticky top-0 z-10 flex items-start justify-between p-6 border-b border-[#1e293b] bg-[#0d0d0d]">
          <div>
            {project.featured && (
              <span className="inline-block mb-2 px-2 py-0.5 rounded-full text-xs font-mono font-semibold bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
                ★ Featured
              </span>
            )}
            <h2 className="text-2xl font-bold text-[#f1f5f9]">{project.title}</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="p-2 rounded-lg border border-[#1e293b] bg-[#111111] hover:border-indigo-500/50 text-[#94a3b8] hover:text-indigo-400 transition-all duration-200 flex-shrink-0 ml-4"
          >
            <X size={17} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Diagram placeholder */}
          <div className="w-full h-36 rounded-xl border border-[#1e293b] bg-[#111111] flex flex-col items-center justify-center gap-2">
            <span className="font-mono text-3xl text-indigo-500/20">{'{}'}</span>
            <p className="text-[#94a3b8] text-xs font-mono">// architecture diagram placeholder</p>
          </div>

          <Section label="Overview" color="text-indigo-400">
            <p className="text-[#94a3b8] leading-relaxed">{project.description}</p>
          </Section>

          <Section label="The Problem" color="text-cyan-400">
            <p className="text-[#94a3b8] leading-relaxed">{project.problem}</p>
          </Section>

          <Section label="Approach" color="text-green-400">
            <p className="text-[#94a3b8] leading-relaxed">{project.approach}</p>
          </Section>

          <Section label="Key Technical Decisions" color="text-indigo-400">
            <ul className="space-y-2">
              {project.decisions.map((d, i) => (
                <li key={i} className="flex items-start gap-3 text-[#94a3b8] text-sm">
                  <ChevronRight size={13} className="text-indigo-400 flex-shrink-0 mt-0.5" />
                  {d}
                </li>
              ))}
            </ul>
          </Section>

          <Section label="Tech Stack" color="text-[#94a3b8]">
            <div className="flex flex-wrap gap-2">
              {project.tech.map(t => (
                <span key={t} className="px-3 py-1 rounded-full text-xs font-mono border border-[#1e293b] bg-[#111111] text-cyan-400">
                  {t}
                </span>
              ))}
            </div>
          </Section>

          {/* Links */}
          <div className="flex gap-3 pt-2">
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[#1e293b] bg-[#111111] hover:border-indigo-500/50 text-[#f1f5f9] text-sm font-medium transition-all duration-200 hover:-translate-y-0.5"
            >
              <Github size={14} /> View on GitHub
            </a>
            {project.demo && (
              <a
                href={project.demo}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-all duration-200 hover:-translate-y-0.5"
              >
                <ExternalLink size={14} /> Live Demo
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function Section({ label, color, children }) {
  return (
    <div>
      <h3 className={`font-mono text-xs uppercase tracking-widest mb-3 ${color}`}>{label}</h3>
      {children}
    </div>
  )
}
